import {TIMEZONES} from 'common/constants';
import {CountType, DateKeyedFunnelSegments, FunnelBookmark, FunnelSegment} from 'common/report/funnels/models/types';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {
  ConversionStepBar,
  ConversionStepBarType,
  NormalConversionStep,
} from 'common/charts/visualization/conversion-column-viz/util';
import {processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {getMedianAccessor} from 'common/report/funnels/models/time-to-convert/utils';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getColumnGroups, getRows} from 'common/report/funnels/charts/funnels-step-chart/table-util';
import {PropertyType, SavedLegend} from 'common/report/constants';
import {StatSigClickHandler} from 'common/report/funnels/charts/funnels-step-chart/cell-renderers';
import {reconcileComparisonSegments} from 'common/report/funnels/util/step-formatter';
import {FunnelTableRow} from 'common/report/funnels/charts/funnels-step-chart/types';
import {ColumnGroup} from 'common/components/data-table/types';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/funnels/models/constants';
import {TABLE_HORIZONTAL_PADDING} from 'common/components/data-table/constants';
import {formatMetricNumber} from 'common/util';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {
  createFunnelDataWithRename,
  getPropertyFilterLabels,
  getSegmentColumnSpecs,
  getSegmentDisplayName,
} from 'common/report/funnels/util';

export const OVERALL_CONTROL_GROUP = [{prop: OVERALL_PROP, propName: OVERALL_PROP_NAME}];

export interface FunnelsStepProjectDetails {
  projectId: string;
  projectAsOfDate: null | string;
  projectTimeZone: typeof TIMEZONES[number];
  utcOffset?: number;
}

export interface StepChartData {
  conversionSteps: Array<NormalConversionStep<never, string>>;
  legendSegmentItems: SegmentItem[];
  detailedLegendSegmentItems: SegmentItem[];
  legendSegmentLabel: string;
  savedLegend: SavedLegend[];
  mainDateRangeLabel: string;
  comparisonDateRangeLabel: null | string;
  comparisonDateRange: null | CalendarDateRange;
  countType: CountType;
  funnelSegmentsForPopover: FunnelSegment[];
  funnelSegments: FunnelSegment[];
  funnelCompareSegments: null | FunnelSegment[];
  funnelLiftSegments: FunnelSegment[] | null;
  funnelMetricLabel?: string;
}

function createFunnelsQuery(bookmark: FunnelBookmark, projectDetails: FunnelsStepProjectDetails): FunnelsQuery {
  return FunnelsQuery.fromBookmark(bookmark, parseInt(projectDetails.projectId, 10));
}

function getConversionStepBars(chartStepIdx: number, data: FunnelSegment[]): Array<ConversionStepBar<string>> {
  return data.map((breakdownSegment) => {
    return {
      conversionRate: breakdownSegment.steps[chartStepIdx].step_conv_ratio,
      displayName: breakdownSegment.propName,
      value: breakdownSegment.steps[chartStepIdx].count,
      extras: breakdownSegment.prop,
    };
  });
}

function getConversionStepNameInfo(
  chartStepIdx: number,
  bookmark: FunnelBookmark,
  response: FunnelQueryResponse,
  projectDetails: FunnelsStepProjectDetails,
): {
  name: string;
  longName: string;
} {
  const query = createFunnelsQuery(bookmark, projectDetails);
  const labels: string[] = query.getStepLabels();
  const isAnyOrderFunnel = query.isAnyOrderFunnel();

  const name = labels[chartStepIdx];

  let longName: string = name;
  if (!isAnyOrderFunnel) {
    const segmentData: DateKeyedFunnelSegments[] = createFunnelDataWithRename(response.data, query);
    const step = segmentData?.[0]?.segments?.[0]?.steps?.[chartStepIdx];
    if (step) {
      const propertyFilterText = getPropertyFilterLabels(step).propertyFilterText;
      if (propertyFilterText) {
        longName = `${longName} when ${propertyFilterText}`;
      }
    }
  }

  return {name, longName};
}

export function getBreakdownSegmentProp(
  conversionStep: NormalConversionStep<never, string>,
  stepBarIndex: number,
): string {
  return conversionStep.bars[stepBarIndex].extras;
}

export function getMainBarIndex(
  conversionStep: NormalConversionStep<never, string>,
  breakdownSegmentProp: string,
): number {
  return conversionStep.bars.findIndex((stepBar, barIndex) => {
    const matchesIndex = getBreakdownSegmentProp(conversionStep, barIndex) === breakdownSegmentProp;
    const isMain = stepBar.type !== ConversionStepBarType.Comparison;
    return matchesIndex && isMain;
  });
}

export function getComparisonBarIndex(
  conversionStep: NormalConversionStep<never, string>,
  breakdownSegmentProp: string,
): number {
  return conversionStep.bars.findIndex((stepBar, barIndex) => {
    const matchesIndex = getBreakdownSegmentProp(conversionStep, barIndex) === breakdownSegmentProp;
    const isComparison = stepBar.type === ConversionStepBarType.Comparison;
    return matchesIndex && isComparison;
  });
}

function getSegmentLabelWithConversionRate(
  segmentItem: SegmentItem,
  lastSegment: FunnelSegment | null,
  formattedCompareData: FunnelSegment[] | null,
): string {
  if (lastSegment) {
    const conversion = formatMetricNumber(lastSegment.steps[lastSegment.steps.length - 1].overall_conv_ratio * 100);
    const conversionLabel = `${lastSegment.propName} • ${conversion}%`;

    if (formattedCompareData) {
      const compareSegment = Object.values(formattedCompareData).find((value) => value.prop === lastSegment.prop);
      const previousConversion = formatMetricNumber(
        compareSegment.steps[compareSegment.steps.length - 1].overall_conv_ratio * 100,
      );

      return `${conversionLabel} (${previousConversion}%)`;
    }

    return conversionLabel;
  }

  return segmentItem.label;
}

export function getDataTableInfo({
  bookmark,
  projectDetails,
  onStatSigClick,
  formattedData,
  formattedCompareData,
  formattedLiftData,
  isDownloadMode,
  savedLegend,
  tableWidth,
  showStepNamesInGroupHeaders,
}: {
  bookmark: FunnelBookmark;
  projectDetails: FunnelsStepProjectDetails;
  onStatSigClick: StatSigClickHandler;
  formattedData: FunnelSegment[];
  formattedCompareData: null | FunnelSegment[];
  formattedLiftData: null | FunnelSegment[];
  isDownloadMode: boolean;
  savedLegend?: SavedLegend[];
  tableWidth?: number;
  showStepNamesInGroupHeaders?: boolean;
}): {rows: FunnelTableRow[]; columnGroups: ColumnGroup[]; comparisonRows?: FunnelTableRow[]} {
  const query = FunnelsQuery.createFromUpdate(createFunnelsQuery(bookmark, projectDetails), {
    controlGroup: bookmark.stat_sig_control ?? OVERALL_CONTROL_GROUP.map((p) => p.prop),
  });
  const shouldShowStatSig = query.shouldShowStatSig();
  const segmentSpec = getSegmentColumnSpecs(bookmark);

  const columnGroups = getColumnGroups({
    onStatSigClick,
    segmentSpec,
    stepNames: query.getStepLabels(),
    segmentTypes: query.getSegmentTypes() as PropertyType[],
    selectedColumns: query.selectedTableColumns,
    // getColumnGroups (which is still used in legacy views) doesn't account for the Data Table widget's own padding, so we manually subtract 16px here
    tableWidth: tableWidth - TABLE_HORIZONTAL_PADDING * 2,
    shouldShowStatSig,
    showStepNamesInGroupHeaders,
    liftType: bookmark?.liftComparison?.type,
  });

  // the "selected" color Map assigns "tbd" (an arbitrary invalid string) for all keys because the generated table row color is expected to be overridden by SegmentFilterController in <mp-segment-filter-data-table>
  // but getRows() is an older method (still used outside this widget) that expects a color string
  const selected = savedLegend
    ? new Map<string, string>(savedLegend[0].selected.map((colorChoice) => [colorChoice.key, `tbd`]))
    : new Map();

  const rows: FunnelTableRow[] = getRows({
    selected,
    controlGroup: query.controlGroup,
    shouldShowStatSig,
    segmentSteps: formattedData,
    shouldFilterForSelected: isDownloadMode,
    liftComparison: bookmark?.liftComparison,
    liftSegmentSteps: formattedLiftData,
    compareSegmentSteps: formattedCompareData,
  });
  if (formattedCompareData && !formattedLiftData) {
    const comparisonRows = getRows({
      selected,
      controlGroup: query.controlGroup,
      shouldShowStatSig,
      segmentSteps: reconcileComparisonSegments({
        segments: formattedData,
        comparisonSegments: formattedCompareData,
        updatePropName: true,
      }),
      shouldFilterForSelected: isDownloadMode,
    });
    return {rows, comparisonRows, columnGroups};
  }
  return {rows, columnGroups};
}

export function getLegendItemsWithDataTableRows({
  rows,
  comparisonRows,
  legendSegmentItems,
}: {
  rows: FunnelTableRow[];
  comparisonRows?: FunnelTableRow[];
  legendSegmentItems: SegmentItem[];
}) {
  const tableRowLookup = new Map<string, FunnelTableRow>(rows.map((row) => [row.identifier, row]));
  const tableComparisonRowLookup = new Map<string, FunnelTableRow>(comparisonRows?.map((row) => [row.identifier, row]));

  return legendSegmentItems
    .filter((legendItem) => tableRowLookup.has(legendItem.id))
    .map(
      (legendItem): SegmentItem => {
        return {
          ...legendItem,
          tableRow: tableRowLookup.get(legendItem.id),
          comparisonTableRow: tableComparisonRowLookup.get(legendItem.id),
        };
      },
    );
}

export function getStepChartData(
  bookmark: FunnelBookmark,
  response: FunnelQueryResponse,
  projectDetails: FunnelsStepProjectDetails,
): StepChartData {
  const stepIndex = bookmark.eventIndex || 0;
  const {legendSegmentItems, stateUpdates, savedLegend} = processFunnelForChartWidget(
    bookmark,
    response,
    projectDetails.projectId,
    projectDetails.projectAsOfDate,
    stepIndex,
    getMedianAccessor(stepIndex),
    projectDetails.projectTimeZone,
  );

  const {dateRanges, dateRangeLabels, formattedData, formattedCompareData, formattedLiftData, query} = stateUpdates;

  // Transpose `formattedData` (an array of breakdown segments, each with multiple steps)
  // into `conversionSteps` (an array of steps, each with multiple breakdown segments).
  //
  // Note: funnels doesn't use placeholder steps, only normal steps
  const conversionSteps: Array<NormalConversionStep<never, string>> =
    formattedData?.[0]?.steps?.map((_, chartStepIdx) => {
      const mainBars = getConversionStepBars(chartStepIdx, formattedData);
      const comparisonBars = getConversionStepBars(chartStepIdx, formattedCompareData ?? []).map((bar) => ({
        ...bar,
        type: ConversionStepBarType.Comparison,
      }));

      const nameInfo = getConversionStepNameInfo(chartStepIdx, bookmark, response, projectDetails);
      return {
        displayName: nameInfo.name,
        tooltipName: nameInfo.longName,
        bars: mainBars.flatMap((mainBar, barIdx) => {
          const comparisonBar = comparisonBars[barIdx];
          return comparisonBar ? [comparisonBar, mainBar] : [mainBar];
        }),
      };
    }) ?? [];

  const funnelSegmentsForPopover = formattedData.flatMap((segment, segmentIdx) => {
    const comparisonSegment = formattedCompareData?.[segmentIdx];
    return comparisonSegment ? [comparisonSegment, segment] : [segment];
  });

  const segMapping = formattedData.reduce((acc, cur) => {
    if (acc[cur.prop]) {
      acc[cur.prop].push(cur);
    } else {
      acc[cur.prop] = [cur];
    }
    return acc;
  }, {});
  const detailedLegendSegmentItems = legendSegmentItems.map((segmentItem) => {
    const lastSegment = segMapping[segmentItem.id]?.[0];
    const label = getSegmentLabelWithConversionRate(segmentItem, lastSegment, formattedCompareData);
    return {...segmentItem, label};
  });

  const segmentNames = bookmark.group_by;
  let legendSegmentLabel = ``;

  segmentNames?.forEach((name) => {
    let label = getSegmentDisplayName(name);
    if (segmentNames[segmentNames.length - 1] !== name) {
      label = label.concat(`/`);
    }
    legendSegmentLabel = legendSegmentLabel.concat(label);
  });

  return {
    conversionSteps,
    legendSegmentItems,
    detailedLegendSegmentItems,
    legendSegmentLabel,
    savedLegend,
    mainDateRangeLabel: dateRangeLabels[0],
    comparisonDateRangeLabel: formattedCompareData ? dateRangeLabels[1] : null,
    comparisonDateRange: formattedCompareData ? dateRanges[1] : null,
    countType: query.countType,
    funnelSegmentsForPopover,
    funnelSegments: formattedData,
    funnelCompareSegments: formattedCompareData,
    funnelLiftSegments: formattedLiftData,
  };
}
